import { useRef, useEffect } from 'react';

import { useTheme } from 'styled-components';
import { gtmBasicEvent } from '@klv/shared/utils/google-tag-manager';
import MixedMedia from '@klv/molecules/mixed-media';
import Buttons from '@klv/molecules/buttons';
import usePrefersReducedMotion from '@klv/shared/utils/preferedReducedMotion';
import { getColorVariantFromBackground, hash } from '@klv/shared/utils/functions';

import * as s from './styles';

import type { TColorVariant, TColorVariantLegacy } from '@klv/shared/styles/types';
import type { ICustomThemeProps } from '@klv/shared/styles/themes/styled';
import type { IRowProps } from './types';

const toggleSpeed = 350;
const closeDelay = 50;

const Row = ({
  expandedRow, index, isFaqsSection = false, row,  showMediaInline, rowHeadlineTag = 'h2', variant, setExpandedRow,
}: IRowProps) => {
  const theme = useTheme();
  const buttonVariant = theme.accordion[variant as keyof ICustomThemeProps['accordion']].button as TColorVariant;
  const accordionContent = useRef<HTMLDivElement>(null);
  const id = `a-${hash(JSON.stringify(row))}`;
  const measurableContainer = useRef<HTMLDivElement>(null);
  const textContent = useRef(<s.StyledTypography colorVariant={buttonVariant} dangerouslySetInnerHTML={{ __html: row.content }} variant="paragraph2" />);

  const preferedReducedMotion = usePrefersReducedMotion();

  const isExpanded = expandedRow === index;

  const handleClick = () => {
    if (!isExpanded) {
      setExpandedRow(index);

      if (isFaqsSection) {
        gtmBasicEvent({ action: row.title, category: 'faq' });
      }
    }
  };

  useEffect(() => {
    const container = accordionContent.current;
    const measurable = measurableContainer.current;

    if (container && measurable) {
      if (isExpanded) {
        // time to expand the containers
        container.style.display = 'block';

        // expanded is true, calculate the height the accordion should grow to.
        container.style.maxHeight = `${measurable.offsetHeight}px`;

        // once our animation is over set MaxHeight to none so we can resize
        // the window without having to resize our max-height
        setTimeout(() => {
          container.style.maxHeight = 'none';
          // set overflow to visible
          container.style.overflow = 'visible';
        }, toggleSpeed);
      } else {
        // reset the accordion maxHeight to contents height so we can animate to 1px;
        container.style.maxHeight = `${measurable.offsetHeight}px`;
        // set overflow to hidden
        container.style.overflow = 'hidden';

        // reset the accordion to 1px max-height after a brief timeout
        // to allow the css transition to animate
        setTimeout(() => {
          container.style.maxHeight = '0px';
        }, preferedReducedMotion ? 0 : closeDelay);

        // once our animation is complete set display to none.
        setTimeout(() => {
          container.style.display = 'none';
        }, preferedReducedMotion ? 0 : toggleSpeed + closeDelay);
      }
    }

    /**
    * We do not want this function to run in the event that someone changes
    * preferedReducedMotion on their host OS while a page with an accordion is open.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded]);
  return (
    <s.AccordionRow $hasBorder={index} $variant={variant}>
      <s.StyledHeadline
        variant="paragraph1Semibold"
        forwardedAs={rowHeadlineTag}
        colorVariant={getColorVariantFromBackground(variant as TColorVariantLegacy)}
      >
        <s.AccordionTitle
          onClick={() => { handleClick(); }}
          aria-expanded={isExpanded}
          aria-controls={id}
        >
          {row.title}
          <s.StyledIcon
            size={1}
            iconName="ChevronDown"
            fillColor="currentColor"
            $isExpanded={isExpanded}
            $variant={variant}
          />
        </s.AccordionTitle>
      </s.StyledHeadline>
      <s.AccordionContent
        ref={accordionContent}
        $isExpanded={isExpanded}
        aria-hidden={!isExpanded}
        id={id}
        $variant={variant}
      >
        <div ref={measurableContainer}>
          {!showMediaInline && <s.MediaWrapper>
            <MixedMedia mixedMedia={row?.mixedMedia || { mediaType: 'image' }} />
          </s.MediaWrapper>}
          {textContent.current}
          {row?.buttons && <s.ButtonWrapper><Buttons buttons={row.buttons} variants={['tertiary']} buttonColor={buttonVariant} /></s.ButtonWrapper>}
          {showMediaInline && <s.InlineMediaWrapper><MixedMedia mixedMedia={row?.mixedMedia || { mediaType: 'image' }} /></s.InlineMediaWrapper>}
        </div>
      </s.AccordionContent>
    </s.AccordionRow>
  );
};

export default Row;
